import dayjs, { Dayjs } from "dayjs";

/**
 * Check if a value is an ISO 8601 date string.
 * Matches formats like: "2024-01-15", "2024-01-15T10:30:00", "2024-01-15T10:30:00.000Z"
 */
export const isISODateString = (value: unknown): value is string => {
  if (typeof value !== "string") return false;
  // Match ISO 8601 date format: YYYY-MM-DD with optional time component
  const isoPattern = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?/;
  return isoPattern.test(value) && !isNaN(Date.parse(value));
};

/**
 * Check if a value is a time-only string.
 * Matches formats like: "08:30", "08:30:00", "1970-01-01T08:30:00"
 * The ISO datetime with 1970-01-01 prefix is commonly used by Aras for time fields.
 */
export const isTimeString = (value: unknown): value is string => {
  if (typeof value !== "string") return false;
  // Match time-only format: HH:mm or HH:mm:ss
  const timeOnlyPattern = /^\d{2}:\d{2}(:\d{2})?$/;
  // Match ISO datetime with 1970-01-01 prefix (Aras time field format)
  const arasTimePattern = /^1970-01-01T\d{2}:\d{2}:\d{2}/;
  return timeOnlyPattern.test(value) || arasTimePattern.test(value);
};

/**
 * Parse time string to dayjs object.
 * Handles various time formats from Aras Innovator.
 *
 * @param value - Time string in format "HH:mm", "HH:mm:ss", or "1970-01-01THH:mm:ss"
 * @returns dayjs object or null if invalid
 */
export const parseTimeString = (value: string): Dayjs | null => {
  // Handle ISO datetime with 1970-01-01 prefix (Aras format)
  if (value.startsWith("1970-01-01T")) {
    const timePart = value.split("T")[1];
    return dayjs(`1970-01-01T${timePart}`);
  }
  // Handle HH:mm or HH:mm:ss format
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(value)) {
    return dayjs(`1970-01-01T${value}`);
  }
  return null;
};

/**
 * Format time value to HH:mm string.
 * Used for displaying time fields in the UI.
 *
 * @param value - Time value (string or dayjs)
 * @returns Formatted time string "HH:mm"
 */
export const formatTimeToHHmm = (value: unknown): string => {
  if (!value) return "";

  // If dayjs object
  if (dayjs.isDayjs(value)) {
    return value.format("HH:mm");
  }

  const strValue = String(value);

  // Handle ISO datetime (e.g., "2026-02-02T08:30:00" or "1970-01-01T08:30:00")
  if (strValue.includes("T")) {
    const timePart = strValue.split("T")[1];
    const [hours, minutes] = timePart.split(":");
    return `${hours}:${minutes}`;
  }

  // Handle HH:mm:ss or HH:mm format
  const [hours, minutes] = strValue.split(":");
  return `${hours}:${minutes}`;
};

/**
 * Transform a record's ISO date string fields to dayjs objects.
 * Used for form initialization where DatePicker/TimePicker components expect dayjs.
 *
 * @param record - The record object from API
 * @returns A new object with date/time strings transformed to dayjs
 */
export const transformDatesToFormValues = (
  record: Record<string, unknown>,
): Record<string, unknown> => {
  const transformed: Record<string, unknown> = {};

  Object.entries(record).forEach(([key, value]) => {
    if (isTimeString(value)) {
      // Handle time-only values
      const parsed = parseTimeString(value);
      transformed[key] = parsed || value;
    } else if (isISODateString(value)) {
      transformed[key] = dayjs(value);
    } else {
      transformed[key] = value;
    }
  });

  return transformed;
};

/**
 * Check if a dayjs value represents a time-only field.
 * Time-only fields are stored with 1970-01-01 as the date component.
 */
const isTimeOnlyValue = (dayjsValue: Dayjs): boolean => {
  return (
    dayjsValue.year() === 1970 &&
    dayjsValue.month() === 0 &&
    dayjsValue.date() === 1
  );
};

/**
 * Transform a record's dayjs fields back to date strings for API.
 * Uses local date format (YYYY-MM-DD) to preserve the exact date selected by user.
 *
 * NOTE: toISOString() converts to UTC which can shift the date by a day
 * depending on timezone. We use format() to keep local date/time.
 *
 * @param values - The form values object
 * @param timeFields - Optional array of field names that are time-only fields
 * @returns A new object with dayjs objects transformed to date strings
 */
export const transformDatesToAPIValues = (
  values: Record<string, unknown>,
  timeFields?: string[],
): Record<string, unknown> => {
  const transformed: Record<string, unknown> = {};

  Object.entries(values).forEach(([key, value]) => {
    if (dayjs.isDayjs(value)) {
      const dayjsValue = value as Dayjs;

      // Check if this is a time-only field (either explicitly specified or auto-detected)
      // Time fields use 1970-01-01 as the date component
      if (timeFields?.includes(key) || isTimeOnlyValue(dayjsValue)) {
        // Format as time only (HH:mm:ss) for Aras time fields
        // Aras expects "1970-01-01THH:mm:ss" format for time fields
        transformed[key] = `1970-01-01T${dayjsValue.format("HH:mm:ss")}`;
      } else {
        // Check if the dayjs object has time component (not midnight)
        // For date-only fields (midnight), use YYYY-MM-DD format
        // For datetime fields, include time in local timezone
        const hasTime =
          dayjsValue.hour() !== 0 ||
          dayjsValue.minute() !== 0 ||
          dayjsValue.second() !== 0;

        if (hasTime) {
          // Include time in local timezone format (no Z suffix = local time)
          transformed[key] = dayjsValue.format("YYYY-MM-DDTHH:mm:ss");
        } else {
          // Date only - preserve the exact date user selected
          transformed[key] = dayjsValue.format("YYYY-MM-DD");
        }
      }
    } else {
      transformed[key] = value;
    }
  });

  return transformed;
};
